import { FC, ReactNode } from 'react';
import { DataRoomActivityOverview, viewingTimeUnits } from 'shared';

import activeDataRoomsIcon from '../../../../assets/images/data-room-activity/active-data-rooms.svg';
import avgSessionIcon from '../../../../assets/images/data-room-activity/avg-session.svg';
import totalDownloadsIcon from '../../../../assets/images/data-room-activity/total-downloads.svg';
import totalViewingTimeIcon from '../../../../assets/images/data-room-activity/total-viewing-time.svg';
import totalViewsIcon from '../../../../assets/images/data-room-activity/total-views.svg';
import uniqueVisitorsIcon from '../../../../assets/images/data-room-activity/unique-visitors.svg';
import { AnimatedNumber } from '../../../../components/animated-number/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import { StringKey } from '../../../../lang/index';

type MetricPart = { unit?: string; value: number };

// The number and its unit are set in different type, so a duration arrives already split —
// "10 Hrs 4 Mins" is two parts, "8 Mins" is one. Plain words, like `formatViewingTime`.
// The shared viewing-time rule, split into the parts a card sets apart.
export const durationParts = (milliseconds: number): MetricPart[] => viewingTimeUnits(milliseconds);

// Long enough to read the numbers running, short enough not to keep the reader waiting.
export const COUNT_UP_MS = 900;

export const MetricIcon: FC<{ src: string }> = ({ src }) => (
  <img alt="" className="size-6" src={src} />
);

/** A metric with an icon and a label; its value is numbers with units, or anything ready-made. */
export const MetricCard: FC<{
  icon: ReactNode;
  label: StringKey;
  parts?: MetricPart[];
  value?: ReactNode;
}> = ({ icon, label, parts = [], value }) => (
  <div className="flex min-w-0 flex-col gap-2 rounded-xl border border-gray-100 bg-white p-3">
    <div className="flex size-8 items-center justify-center">{icon}</div>
    <div className="flex min-w-0 flex-col gap-1">
      <span className="truncate text-label-sm font-medium text-gray-600">
        <AppFormattedMessage id={label} />
      </span>
      <div className="flex items-baseline gap-1 whitespace-nowrap">
        {value ? (
          <span className="truncate text-xl font-semibold text-[#181D27]">{value}</span>
        ) : (
          parts.map((part) => (
            <span className="text-xl font-semibold text-[#181D27]" key={part.unit ?? 'value'}>
              {/* The numbers count up from zero as they arrive, and a new window counts on from
                  them rather than swapping them. */}
              <AnimatedNumber countFrom={0} duration={COUNT_UP_MS} integer value={part.value} />
              {part.unit && (
                <span className="ml-1 text-label-sm font-medium text-gray-500">{part.unit}</span>
              )}
            </span>
          ))
        )}
      </div>
    </div>
  </div>
);

/** The half-gray panel the metric cards sit in, with whatever heads it above the grid. */
export const MetricPanel: FC<{ children: ReactNode; header?: ReactNode }> = ({
  children,
  header,
}) => (
  <div className="flex flex-col gap-4 rounded-lg border border-gray-100 bg-gray-50/50 p-4">
    {header}
    <div className="grid grid-cols-[repeat(auto-fit,minmax(140px,1fr))] gap-3">{children}</div>
  </div>
);

/**
 * 20.1.1 §3. Active Data Rooms is current state and the other five are the window's — the one
 * distinction that matters here, and the reason the toggle above does not touch the first card.
 */
export const ActivityMetricCards: FC<{ overview: DataRoomActivityOverview }> = ({ overview }) => {
  const { activeDataRooms, totals } = overview;

  return (
    <MetricPanel>
      <MetricCard
        icon={<MetricIcon src={activeDataRoomsIcon} />}
        label="ACTIVE_DATA_ROOMS"
        parts={[{ value: activeDataRooms }]}
      />
      <MetricCard
        icon={<MetricIcon src={uniqueVisitorsIcon} />}
        label="UNIQUE_VISITORS"
        parts={[{ value: totals.uniqueVisitors }]}
      />
      <MetricCard
        icon={<MetricIcon src={totalViewsIcon} />}
        label="TOTAL_VIEWS"
        parts={[{ value: totals.views }]}
      />
      <MetricCard
        icon={<MetricIcon src={totalDownloadsIcon} />}
        label="TOTAL_DOWNLOADS"
        parts={[{ value: totals.downloads }]}
      />
      <MetricCard
        icon={<MetricIcon src={totalViewingTimeIcon} />}
        label="TOTAL_VIEWING_TIME"
        parts={durationParts(totals.viewingTimeMs)}
      />
      <MetricCard
        icon={<MetricIcon src={avgSessionIcon} />}
        label="AVG_SESSION"
        parts={durationParts(totals.avgSessionMs)}
      />
    </MetricPanel>
  );
};
